/**
 * What the map's two WebGL layers must agree on (amendments 14 and 15): one heartbeat, one tilt
 * and one height per cell.
 *
 * The columns bounce when the pulse reaches them and the pulse's links run between their tops,
 * so anything the two worked out separately — a step a frame off, a height a metre different —
 * would show as a glitch. Every number both of them use is here, and the shaders take the same
 * formulas from `GLSL_MOTION` rather than restating them.
 */

import { type Cell, cellPadMetres, resolutionOf } from '$domain/h3.ts';
import { glsl } from './webgl.ts';

/** Seconds from one heartbeat to the next. */
export const PERIOD = 4.5;
/** Seconds the pulse takes from a member to the next one out. */
export const STEP = 0.24;
/** Seconds a member glows once the pulse has reached it. */
export const FLASH = 0.85;
/**
 * Members further out than this light up together with it. Past ten steps a wave is a queue,
 * and the map would be drawing for the whole period.
 */
export const MAX_HOP = 10;
/** Σ GDI at which a swarm pulses at full strength: five cells at 90, or nine at 50. */
export const FULL_STRENGTH = 450;
/** How far above its height a column rises when the pulse reaches it. */
const BOUNCE = 0.12;
/** The tilt, in degrees, over which the columns grow out of the ground. */
const RISE_FROM = 8;
const RISE_TO = 38;

/** Seconds into the current heartbeat. */
export const heartbeat = (now: number) => (now / 1000) % PERIOD;

const smooth = (from: number, to: number, value: number) => {
  const t = Math.min(1, Math.max(0, (value - from) / (to - from)));
  return t * t * (3 - 2 * t);
};

/**
 * How far the columns have risen at this tilt: 0 on a flat map, which therefore looks exactly
 * as it always has, and 1 from `RISE_TO` on. Tilting is what animates them.
 */
export const liftFor = (pitch: number) => smooth(RISE_FROM, RISE_TO, pitch);

/**
 * A column's full height in metres.
 *
 * In proportion to its cell rather than absolute, so that the overview's res-7 columns, seven
 * times as wide, stand in the same proportion as the res-9 ones instead of as flat tiles. At
 * res 9 a Hotspot comes to about 110 m: a tower block among the four-storey houses round it,
 * which reads as "here" from across the city without walling off the street you look down.
 * Twice that, tried first, swallowed the view at street zoom. Even a grey cell keeps a plinth.
 */
export function columnMetres(cell: Cell, gdi: number): number {
  // `cellPadMetres` is twice the average edge, and a hexagon's edge is its circumradius.
  const radius = cellPadMetres(resolutionOf(cell)) / 2;
  return radius * (0.08 + (0.55 * Math.min(100, Math.max(0, gdi))) / 100);
}

/** The same timing for the shaders. A member outside any swarm has `hop` -1 and never moves. */
export const GLSL_MOTION = `
const float STEP = ${glsl(STEP)};
const float FLASH = ${glsl(FLASH)};
const float BOUNCE = ${glsl(BOUNCE)};
// Seconds since the pulse reached a member hop steps out; negative before it has.
float sinceHit(float tau, float hop) {
  return tau - hop * STEP;
}
// 1 the moment the pulse arrives, fading to 0 over FLASH.
float flashOf(float tau, float hop) {
  float since = sinceHit(tau, hop);
  return hop >= 0.0 && since >= 0.0 && since <= FLASH ? 1.0 - since / FLASH : 0.0;
}
// Up and back down again over FLASH.
float bounceOf(float tau, float hop) {
  float since = sinceHit(tau, hop);
  return hop >= 0.0 && since >= 0.0 && since <= FLASH ? sin(3.14159265 * since / FLASH) : 0.0;
}
// Stronger with every member the pulse has passed, and with the swarm as a whole.
float amplitude(float hop, float strength) {
  return min(1.5, 0.55 + 0.2 * max(hop, 0.0)) * (0.55 + 0.45 * strength);
}
`;
